import type { Organization } from "@mountaya/client";

import type { RestClientConfig } from "../../rest/rules/sdk";
import { organizationsFor } from "./sdk";

/**
 * organizationLookupQueryKey is the TanStack Query key for a publishable-key org
 * lookup. Keyed by pkey so two embeds with different keys do not share a cache
 * entry. Defined in this hookless layer so both a future useQuery hook and
 * render-free callers (the embed route loader) share one source of truth.
 */
export const organizationLookupQueryKey = (publishableKey: string) =>
  ["organizations", "lookup", publishableKey] as const;

/**
 * fetchOrganizationLookup resolves an organization from a publishable key via the
 * public POST /v1/organizations/lookup endpoint. It is a hookless query-fn: it carries no
 * React-hook imports so the render-free embed route loader can call it directly (the
 * depcruise hookless rule).
 *
 * It goes through the generated `postOrganizationLookup`, which the contract declares as taking
 * no credential at all (`security: []`): the key to resolve rides the body, which is where the
 * backend reads it, and the request carries nothing the client's credential holds, no
 * `X-API-Key` header and no cookie, whatever the config's strategy. That is the point of the
 * endpoint: it resolves a key before anything has presented it as a credential, so nothing here
 * acts as anybody, and the client's member-session guard, which reads the session a credential
 * holds, has nothing to refuse.
 *
 * It is a POST that only reads, and it is declared idempotent so the transport's retry budget
 * covers it. The backend returns 403 when the key reaches no `publishing` scope and 404 when
 * it is unknown or revoked; both throw the transport's typed error, so TanStack Query drives its
 * error state and the route branches on the class.
 */
export const fetchOrganizationLookup = async (
  config: RestClientConfig,
  publishableKey: string,
): Promise<Organization> => {
  const body = await organizationsFor(config).postOrganizationLookup({
    body: { publishable_key: publishableKey },
  });

  return body.data;
};

/**
 * organizationLookupQueryOptions bundles the pkey-scoped query key + hookless
 * fetch fn for queryClient.ensureQueryData (the embed route loader) and a future
 * useQuery hook alike, so the render-free loader and the React layer share one
 * cache entry.
 */
export const organizationLookupQueryOptions = (
  config: RestClientConfig,
  publishableKey: string,
) => ({
  queryFn: () => fetchOrganizationLookup(config, publishableKey),
  queryKey: organizationLookupQueryKey(publishableKey),
});
